import { Box } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useCallback, useEffect, useRef, useState } from "react";
import RenderInput from "./RenderInput";
import Row from "./Row";
import { Loader } from "Shared/Common/Loader";
import useLang from "Shared/hooks/useLanguage";
import NoRecord from "Shared/Common/NoRecord";
import { ArrowDown, ArrowUp } from "Shared/Icons";

interface ReqDataGridProps {
  tabsInfo: any;
  labTimeZone?: string | null;
  rowInfo: any;
  value: number;
  loading: boolean;
  filterData: any;
  searchValue: any;
  setSearchValue: any;
  setFilterData: any;
  setCurPage: any;
  triggerFetch: () => void;
  selectedRows: any[];
  onRowCheck: (checked: boolean, row: any) => void;
  onAllCheck: (checked: boolean) => void;
}

const ReqDataGrid = (props: ReqDataGridProps) => {
  const { t } = useLang();
  const {
    tabsInfo,
    labTimeZone,
    rowInfo,
    loading,
    filterData,
    searchValue,
    setSearchValue,
    setFilterData,
    selectedRows,
    onRowCheck,
    onAllCheck,
  } = props;

  const [currentColumnKey, setCurrentColumnKey] = useState("");
  const searchRef = useRef<any>(null);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const topScrollBarRef = useRef<HTMLDivElement>(null);
  const bottomScrollBarRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(0);
  const [showScrollBar, setShowScrollBar] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const isScrollingRef = useRef(false);

  const updateTableWidth = useCallback(() => {
    if (tableRef.current) {
      setTableWidth(tableRef.current.scrollWidth);
    }
  }, []);

  const checkScrollbars = useCallback(() => {
    if (tableContainerRef.current && tableRef.current) {
      const containerWidth = tableContainerRef.current.clientWidth;
      const tw = tableRef.current.scrollWidth;
      setShowScrollBar(tw > containerWidth);
    }
  }, []);

  const updateAllScrollPositions = useCallback((position: number) => {
    setScrollPosition(position);
    if (tableContainerRef.current)
      tableContainerRef.current.scrollLeft = position;
    if (topScrollBarRef.current) topScrollBarRef.current.scrollLeft = position;
    if (bottomScrollBarRef.current)
      bottomScrollBarRef.current.scrollLeft = position;
  }, []);

  useEffect(() => {
    updateTableWidth();
    checkScrollbars();
    if (tableRef.current) {
      const observer = new MutationObserver(() => {
        updateTableWidth();
        checkScrollbars();
      });
      observer.observe(tableRef.current, { childList: true, subtree: true });
      return () => observer.disconnect();
    }
    const handleResize = () => {
      updateTableWidth();
      checkScrollbars();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateTableWidth, checkScrollbars]);

  const syncTableScrollFromTop = useCallback(() => {
    if (isScrollingRef.current) return;
    isScrollingRef.current = true;
    if (topScrollBarRef.current)
      updateAllScrollPositions(topScrollBarRef.current.scrollLeft);
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 10);
  }, [updateAllScrollPositions]);

  const syncTableScrollFromBottom = useCallback(() => {
    if (isScrollingRef.current) return;
    isScrollingRef.current = true;
    if (bottomScrollBarRef.current)
      updateAllScrollPositions(bottomScrollBarRef.current.scrollLeft);
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 10);
  }, [updateAllScrollPositions]);

  const syncScrollBars = useCallback(() => {
    if (isScrollingRef.current) return;
    isScrollingRef.current = true;
    if (tableContainerRef.current)
      updateAllScrollPositions(tableContainerRef.current.scrollLeft);
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 10);
  }, [updateAllScrollPositions]);

  const [isVisibleTopScroll, setIsVisibleTopScroll] = useState(false);
  const [isVisibleBottomScroll, setIsVisibleBottomScroll] = useState(false);
  const [isStickyBottom, setIsStickyBottom] = useState(false);
  const BAR_HEIGHT = 20;

  useEffect(() => {
    const handleScroll = () => {
      if (tableContainerRef.current) {
        const tableRect = tableContainerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const isTableVisible =
          tableRect.bottom > 0 && tableRect.top < windowHeight;
        const isHeaderAboveView = tableRect.top < 80;
        setIsVisibleTopScroll(
          isTableVisible && isHeaderAboveView && showScrollBar
        );
        const isTableBottomVisible = tableRect.bottom > 100;
        setIsVisibleBottomScroll(
          isTableVisible && isTableBottomVisible && showScrollBar
        );
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [showScrollBar]);

  useEffect(() => {
    const calc = () => {
      if (!tableContainerRef.current) return;
      const rect = tableContainerRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const isTableVisible = rect.bottom > 0 && rect.top < vh;
      setIsVisibleBottomScroll(showScrollBar && isTableVisible);
      setIsStickyBottom(
        showScrollBar && isTableVisible && rect.bottom > vh + 1
      );
    };
    calc();
    window.addEventListener("scroll", calc, { passive: true });
    window.addEventListener("resize", calc);
    return () => {
      window.removeEventListener("scroll", calc);
      window.removeEventListener("resize", calc);
    };
  }, [showScrollBar, rowInfo]);

  useEffect(() => {
    if (topScrollBarRef.current)
      topScrollBarRef.current.scrollLeft = scrollPosition;
    if (bottomScrollBarRef.current)
      bottomScrollBarRef.current.scrollLeft = scrollPosition;
    if (tableContainerRef.current)
      tableContainerRef.current.scrollLeft = scrollPosition;
  }, [scrollPosition, isVisibleTopScroll, isVisibleBottomScroll]);

  const sortData = (tabsDetail: any) => {
    const newDirection = searchRef.current?.id === "asc" ? "desc" : "asc";
    searchRef.current.id = newDirection;
    setFilterData((prev: any) => ({
      ...prev,
      sortColumn: tabsDetail?.columnKey,
      sortDirection: newDirection,
      pageNumber: 1,
    }));
    if (props?.setCurPage) props.setCurPage(1);
    props.triggerFetch();
  };

  const isColumnVisible = (col: any) =>
    col?.isShowOnUi && col?.isShow && !col?.isExpandData;

  const allChecked =
    rowInfo?.length > 0 && selectedRows.length === rowInfo.length;

  return (
    <div>
      <Box sx={{ height: "auto", width: "100%" }}>
        <div className="w-full relative">
          {showScrollBar && isVisibleTopScroll && (
            <div
              ref={topScrollBarRef}
              className="overflow-auto w-full custom-scroll-nk sticky top-0 z-10"
              style={{
                height: "16px",
                marginBottom: "4px",
                backgroundColor: "white",
                borderTop: "1px solid #e5e7eb",
                borderBottom: "1px solid #e5e7eb",
              }}
              onScroll={syncTableScrollFromTop}
            >
              <div style={{ width: `${tableWidth}px`, height: "1px" }} />
            </div>
          )}

          <div className="table_bordered overflow-hidden">
            <div
              ref={tableContainerRef}
              className="overflow-auto max-h-96 w-full"
              onScroll={syncScrollBars}
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                paddingBottom: isStickyBottom ? `${BAR_HEIGHT}px` : 0,
              }}
            >
              <Table
                ref={tableRef}
                aria-label="table with sticky header"
                stickyHeader
                className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
              >
                {!tabsInfo ? null : (
                  <TableHead>
                    <TableRow className="h-40px">
                      <TableCell />
                      {tabsInfo?.map((tabsDetail: any) => (
                        <React.Fragment key={tabsDetail.columnKey}>
                          {isColumnVisible(tabsDetail) && (
                            <TableCell sx={{ width: "max-content" }}>
                              <div className="d-flex justify-content-center align-items-center">
                                <div style={{ width: "max-content" }}>
                                  {tabsDetail?.filterColumnsType && (
                                    <RenderInput
                                      tabsDetail={tabsDetail}
                                      currentColumnKey={currentColumnKey}
                                      setCurrentColumnKey={setCurrentColumnKey}
                                      tabValue={props.value}
                                      triggerFetch={props?.triggerFetch}
                                      setCurPage={props?.setCurPage}
                                      filterData={filterData}
                                      setFilterData={setFilterData}
                                      searchValue={searchValue}
                                      setSearchValue={setSearchValue}
                                    />
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <label className="form-check form-check-sm form-check-solid d-flex justify-content-center">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={allChecked}
                            onChange={(e) => onAllCheck(e.target.checked)}
                          />
                        </label>
                      </TableCell>
                      {tabsInfo?.map((tabsDetail: any) => (
                        <React.Fragment key={tabsDetail.columnKey}>
                          {isColumnVisible(tabsDetail) && (
                            <TableCell sx={{ width: "max-content" }}>
                              <div
                                onClick={() => sortData(tabsDetail)}
                                className="d-flex justify-content-between cursor-pointer"
                                ref={searchRef}
                              >
                                <div style={{ width: "max-content" }}>
                                  {t(tabsDetail?.columnLabel)}{" "}
                                </div>
                                <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                  <ArrowUp
                                    CustomeClass={`${
                                      filterData.sortDirection === "asc" &&
                                      filterData.sortColumn ===
                                        tabsDetail.columnKey
                                        ? "text-danger fs-6"
                                        : "text-gray-700 fs-7"
                                    }  p-0 m-0 "`}
                                  />
                                  <ArrowDown
                                    CustomeClass={`${
                                      filterData.sortDirection === "desc" &&
                                      filterData.sortColumn ===
                                        tabsDetail.columnKey
                                        ? "text-danger fs-6"
                                        : "text-gray-700 fs-7"
                                    }  p-0 m-0`}
                                  />
                                </div>
                              </div>
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={(tabsInfo?.length || 10) + 1}>
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : rowInfo?.length ? (
                    rowInfo.map((RowData: any, idx: number) => (
                      <Row
                        RowData={RowData}
                        tabsInfo={tabsInfo}
                        labTimeZone={labTimeZone}
                        key={RowData?.RequisitionOrderID ?? idx}
                        isChecked={selectedRows.some(
                          (r: any) =>
                            r.requisitionOrderId === RowData?.RequisitionOrderID
                        )}
                        onCheckChange={onRowCheck}
                      />
                    ))
                  ) : (
                    <NoRecord />
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {showScrollBar && isVisibleBottomScroll && (
            <div
              ref={bottomScrollBarRef}
              className="overflow-auto custom-scroll-nk"
              style={{
                height: `${BAR_HEIGHT}px`,
                marginTop: "4px",
                backgroundColor: "white",
                borderTop: "1px solid #e5e7eb",
                position: isStickyBottom ? "fixed" : "static",
                bottom: isStickyBottom ? 0 : "auto",
                zIndex: isStickyBottom ? 20 : "auto",
                width: !isStickyBottom ? "100%" : "",
              }}
              onScroll={syncTableScrollFromBottom}
            >
              <div style={{ width: `${tableWidth}px`, height: "1px" }} />
            </div>
          )}
        </div>
      </Box>
    </div>
  );
};

export default React.memo(ReqDataGrid);
